'use client';

import { faHeart as regularHeart } from '@fortawesome/pro-regular-svg-icons';
import { faHeart as solidHeart } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatLocation } from '@pickleballinc/lib/format-location';
import { getCDNURL } from '@pickleballinc/lib/get-cdn-url';
import clsx from 'clsx';
import moment from 'moment';
import { useCallback, useMemo, useState } from 'react';

import CustomLink from '../../ui/custom-link';
import { TEAM_LEAGUES_PLACEHOLDER_IMAGE } from '../utils/constants';

export interface Logo {
	uuid: string;
	url: string;
}

interface StateInfo {
	id: string;
	abbreviation: string;
	title: string;
}

interface CountryInfo {
	abbreviation: string;
	title: string;
}
export interface Location {
	city: string;
	stateInfo: StateInfo;
	countryInfo: CountryInfo;
}

export interface League {
	uuid: string;
	playerCount: number;
	isFavourite: boolean;
	logo: Logo;
	startDatetime: string;
	endDatetime: string;
	title: string;
	location: Location | null;
	organizationUuid: string;
	slug: string;
	isSeasonActive: boolean;
	registrationDateClose?: string | null;
	registrationDateOpen?: string | null;
	hidePlayersCount?: boolean;
	hideTeamsCount?: boolean;
	seasonStandingsExist: boolean;
}

interface LeagueCardProps {
	league: League;
	isHeightFitContent?: boolean;
	isMLP?: boolean;
	className?: string;
	isSearchCard?: boolean;
	teamLeaguesBaseUrl: string;
	handleFavoriteToggle?: (e: React.MouseEvent<HTMLButtonElement | HTMLDivElement>, isFavourite: boolean, uuid: string) => void;
	isAddingLeagueToFavorite?: boolean;
	isUserLogged?: boolean;
	isFavoritedTab?: boolean;
	isUserAlsoPlayer?: boolean;
	/**
	 * Override the click-through URL. When set, the wrapping `CustomLink` uses this href instead of the
	 * default `${teamLeaguesBaseUrl}/team-leagues/${slug || uuid}…`. Mirrors `TournamentCard.customCardHref`
	 * and `LeagueCard.customCardHref` so callers (like the listing-ad rotator) drive all three cards through
	 * the same prop.
	 */
	customCardHref?: string;
	/**
	 * When paired with `customCardHref`, opens the click-through in a new tab (`target="_blank"` + safe
	 * `rel="noopener noreferrer"`). No effect when `customCardHref` is not set.
	 */
	openInNewTab?: boolean;
	/**
	 * Render the click-through as a plain `<a>` (full document navigation) instead of a Next `<Link>`.
	 * Set by the listing-ad rotator: an ad's URL carries Everflow parameters that the landing middleware
	 * has to see, and Next would route a same-origin URL client-side and skip it.
	 */
	nativeAnchor?: boolean;
	/**
	 * Suppresses the bottom `"Start: <date>"` line under the location row. The top date range
	 * (`"start - end"` / `"Ended on end"`) is still rendered. Used by the clubs listing-ad rotator to
	 * declutter the card for ad placements.
	 */
	hideStartDateLine?: boolean;
}

export const TeamLeagueCard = ({
	league,
	isHeightFitContent,
	isMLP,
	className = '',
	isSearchCard,
	teamLeaguesBaseUrl,
	handleFavoriteToggle,
	isAddingLeagueToFavorite = false,
	isUserLogged,
	isFavoritedTab,
	isUserAlsoPlayer,
	customCardHref,
	openInNewTab,
	nativeAnchor,
	hideStartDateLine
}: LeagueCardProps) => {
	const startDateTime = moment.parseZone(league.startDatetime).format('MMM D YYYY');
	const endDateTime = moment.parseZone(league.endDatetime).format('MMM D YYYY');

	// const now = new Date();

	// const isRegistrationOpen =
	// 	league.registrationDateOpen &&
	// 	league.registrationDateClose &&
	// 	now > new Date(league.registrationDateOpen) &&
	// 	now < new Date(league.registrationDateClose);

	const [isPlaceholderImage, setIsPlaceholderImage] = useState(false);

	const getImageToDisplay = useMemo(() => {
		if (league.logo?.url) {
			return getCDNURL(league.logo?.url, 300, 150);
		}
		setIsPlaceholderImage(true);
		return getCDNURL(TEAM_LEAGUES_PLACEHOLDER_IMAGE, 300, 150);
	}, [league]);

	const onFavoriteClick = useCallback(
		(e: React.MouseEvent<HTMLDivElement>) => {
			if (!isAddingLeagueToFavorite) {
				handleFavoriteToggle?.(e, league.isFavourite, league.uuid);
			}
		},
		[isAddingLeagueToFavorite, handleFavoriteToggle, league.isFavourite, league.uuid]
	);

	// `customCardHref` takes precedence over the default team-league detail URL — same pattern as
	// `TournamentCard` / `LeagueCard`. Used by the listing-ad rotator to point clicks at the ad's
	// destination URL instead of the team-league page.
	const href =
		customCardHref ||
		`${teamLeaguesBaseUrl}/team-leagues/${league.slug || league.uuid}${league.isSeasonActive && league.seasonStandingsExist ? '/standings' : ''}`;

	return (
		<CustomLink
			href={href}
			target={customCardHref && openInNewTab ? '_blank' : undefined}
			rel={customCardHref && openInNewTab ? 'noopener noreferrer' : undefined}
			native={nativeAnchor}
		>
			<div
				className={`relative h-full cursor-pointer overflow-hidden rounded-lg border border-gray-300 ${!isSearchCard ? 'w-[224px]' : ''} h-[322px] ${isMLP ? 'bg-transparent' : 'bg-white hover:bg-gray-200'} ${className}`}
			>
				{!isMLP && !league.hidePlayersCount && league.playerCount > 0 && (
					<div className="absolute left-2 top-2 rounded-[5px] bg-green-600 px-3 py-0.5 text-sm text-white">
						{league.playerCount} players
					</div>
				)}

				{isUserLogged && (!isFavoritedTab || (isUserAlsoPlayer && isFavoritedTab)) ? (
					<div
						onClick={onFavoriteClick}
						className={clsx(
							'absolute right-[6px] top-[6px] transition-transform duration-150',
							isAddingLeagueToFavorite
								? 'pointer-events-none opacity-50 cursor-not-allowed'
								: 'cursor-pointer hover:scale-105 active:scale-95'
						)}
					>
						<div className="flex items-center justify-center h-8 w-8 rounded-md border border-gray-300 bg-white shadow-md">
							<FontAwesomeIcon
								icon={league.isFavourite ? solidHeart : regularHeart}
								className={clsx(
									'text-md transition-colors duration-150 size-5',
									league.isFavourite ? 'text-error-600' : 'text-gray-600'
								)}
							/>
						</div>
					</div>
				) : null}

				<div className={'flex w-full items-center justify-center border-b border-gray-300 bg-white'} style={{ aspectRatio: '16 / 9' }}>
					<img
						src={getImageToDisplay}
						alt="League logo"
						className={clsx('mx-auto size-full object-contain', isPlaceholderImage && 'py-4')}
					/>
				</div>
				<div className={`${isHeightFitContent ? 'h-fit' : 'h-[200px]'} flex w-full flex-col gap-2 p-4 transition-colors`}>
					{league.startDatetime && league.endDatetime ? (
						<p className={`text-sm ${isMLP ? 'font-normal leading-5 text-gray-50' : 'text-gray-500'}`}>
							{`${startDateTime} - ${endDateTime}`}
						</p>
					) : (
						league.endDatetime && <p className={`text-sm ${isMLP ? 'text-white' : 'text-gray-500'}`}>Ended on {endDateTime}</p>
					)}

					<h3
						className={`text-base ${
							isMLP ? 'font-semibold leading-6 text-white' : 'line-clamp-2 font-medium leading-tight text-gray-800'
						}`}
					>
						{league.title}
					</h3>
					{league.location ? (
						<p className={`text-sm ${isMLP ? 'text-white' : 'text-gray-500'}`}>
							{formatLocation([league.location.city, league.location.stateInfo.title, league.location.countryInfo.abbreviation])}
						</p>
					) : null}

					{!isMLP && league.startDatetime && !hideStartDateLine ? (
						<p className={`text-sm ${isMLP ? 'text-white' : 'text-gray-500'}`}>Start: {startDateTime}</p>
					) : null}
					{isMLP ? <p className="text-sm text-white">{league.playerCount} players</p> : null}
				</div>
			</div>
		</CustomLink>
	);
};
